/**
 * Athletes Launch — Analytics Service
 *
 * Single source of truth for all behavioral tracking.
 * Both Firebase Analytics (GA4) and Amplitude receive every event.
 *
 * Usage:
 *   import { EVENTS, trackEvent, identifyUser } from 'services/AnalyticsService';
 *   trackEvent(firebase, EVENTS.PRODUCT_VIEWED, { product_id: '123', price: 49.99 });
 *
 * Or use the useAnalytics() hook for React components.
 *
 * Rules:
 *  - Never throw — analytics must NEVER break app functionality
 *  - All event names are snake_case
 *  - All param keys are snake_case
 *  - All string values are lowercase where possible (avoids split event counts)
 */

// ─── Event Name Constants ──────────────────────────────────────────────────────

export const EVENTS = Object.freeze({
  // ── Auth ──────────────────────────────────────────────────────────────────
  LOGIN:                      'login',
  SIGN_UP:                    'sign_up',
  LOGOUT:                     'logout',
  EMAIL_VERIFIED:             'email_verified',
  PASSWORD_RESET_REQUESTED:   'password_reset_requested',
  SOCIAL_LOGIN_STARTED:       'social_login_started',
  LOGIN_RATE_LIMIT_HIT:       'login_rate_limit_hit',

  // ── Onboarding / Profile ──────────────────────────────────────────────────
  PROFILE_UPDATED:            'profile_updated',
  PROFILE_PHOTO_UPLOADED:     'profile_photo_uploaded',
  ONBOARDING_STEP_COMPLETED:  'onboarding_step_completed',
  SCHOOL_EMAIL_VERIFIED:      'school_email_verified',
  PHONE_VERIFIED:             'phone_verified',

  // ── Marketplace ───────────────────────────────────────────────────────────
  MARKETPLACE_VIEWED:         'marketplace_viewed',
  PRODUCT_VIEWED:             'product_viewed',
  PRODUCT_SEARCHED:           'product_searched',
  CATEGORY_FILTERED:          'category_filtered',
  AFFILIATE_REQUEST_STARTED:  'affiliate_request_started',
  AFFILIATE_REQUEST_SUBMITTED:'affiliate_request_submitted',
  PRODUCT_PURCHASED:          'product_purchased',
  BLAST_SYNDICATOR_OPENED:    'blast_syndicator_opened',
  BLAST_SENT:                 'blast_sent',

  // ── Stripe Connect (Companies) ────────────────────────────────────────────
  STRIPE_CONNECT_STARTED:     'stripe_connect_started',
  STRIPE_CONNECT_RETURNED:    'stripe_connect_returned',
  STRIPE_SYNC_COMPLETED:      'stripe_sync_completed',
  STRIPE_SYNC_FAILED:         'stripe_sync_failed',

  // ── Deal Finder / CRM ─────────────────────────────────────────────────────
  DEAL_FINDER_OPENED:         'deal_finder_opened',
  DEAL_SEARCHED:              'deal_searched',
  DEAL_RESULT_VIEWED:         'deal_result_viewed',
  DEAL_INQUIRY_SENT:          'deal_inquiry_sent',
  CRM_OPENED:                 'crm_opened',
  DEAL_STAGE_CHANGED:         'deal_stage_changed',
  CONTRACT_CREATED:           'contract_created',
  CONTRACT_SENT:              'contract_sent',

  // ── Sponsorships ──────────────────────────────────────────────────────────
  SPONSORSHIP_VIEWED:         'sponsorship_viewed',
  SPONSORSHIP_CREATED:        'sponsorship_created',
  SPONSORSHIP_INQUIRY_SENT:   'sponsorship_inquiry_sent',

  // ── Social / Content ──────────────────────────────────────────────────────
  SOCIAL_ACCOUNT_CONNECTED:   'social_account_connected',
  SOCIAL_ACCOUNT_DISCONNECTED:'social_account_disconnected',
  CONTENT_POST_CREATED:       'content_post_created',
  AUTO_CLIP_SUBMITTED:        'auto_clip_submitted',
  DIGITAL_TWIN_OPENED:        'digital_twin_opened',
  TWIN_CONTENT_GENERATED:     'twin_content_generated',

  // ── Subscriptions / Tokens ────────────────────────────────────────────────
  SUBSCRIPTION_CHECKOUT_STARTED: 'subscription_checkout_started',
  TOKEN_PACK_PURCHASED:          'token_pack_purchased',
  TOKENS_CONSUMED:               'tokens_consumed',

  // ── Navigation / Engagement ───────────────────────────────────────────────
  PAGE_VIEW:                  'page_view',
  SIDEBAR_NAV_CLICKED:        'sidebar_nav_clicked',
  ATHLETE_PROFILE_VIEWED:     'athlete_profile_viewed',
  EXPLORE_TAB_CHANGED:        'explore_tab_changed',
  SEARCH_PERFORMED:           'search_performed',
});

// ─── Core Helpers ──────────────────────────────────────────────────────────────

/**
 * Track a behavioral event to both Firebase Analytics and Amplitude.
 * Safe to call unconditionally — never throws.
 *
 * @param {object} firebase  - Firebase class instance from FirebaseContext
 * @param {string} eventName - One of EVENTS.*
 * @param {object} [params]  - Additional event parameters (snake_case keys)
 */
export function trackEvent(firebase, eventName, params = {}) {
  try {
    if (!firebase || !eventName) return;

    // ── Dev-mode logging ──────────────────────────────────────────────────────
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '%c[ANALYTICS]%c ' + eventName,
        'background:#edd714;color:#000;font-weight:bold;padding:2px 8px;border-radius:4px 0 0 4px;',
        'background:#222;color:#edd714;font-weight:600;padding:2px 8px;border-radius:0 4px 4px 0;',
        Object.keys(params).length ? params : ''
      );
    }

    firebase.trackAnalyticsEvent(eventName, params);
  } catch (err) {
    // Silently swallow — analytics must never break the app
  }
}


/**
 * Identify a user and set their properties in both Firebase Analytics and Amplitude.
 * Call this after login or sign-up once the full user profile is available.
 *
 * @param {object} firebase - Firebase class instance
 * @param {string} uid      - Firebase UID
 * @param {object} traits   - User properties to set (e.g. { user_type, plan, sport })
 */
export function identifyUser(firebase, uid, traits = {}) {
  try {
    if (!firebase || !uid) return;

    // Firebase Analytics
    firebase.analytics.setUserId(uid);
    const safeTraits = {};
    Object.entries(traits).forEach(([k, v]) => {
      if (v !== undefined && v !== null) safeTraits[k] = String(v);
    });
    if (Object.keys(safeTraits).length > 0) {
      firebase.analytics.setUserProperties(safeTraits);
    }

    // Amplitude
    firebase.amplitudeClient.setUserId(uid);
    if (Object.keys(safeTraits).length > 0) {
      firebase.amplitudeClient.setUserProperties(safeTraits);
    }
  } catch (err) {
    // Silently swallow
  }
}
